import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Send,
  Search,
  Globe,
  Volume2,
  Save,
  User,
  MessageCircle,
  Check,
  CheckCheck,
  X,
  ChevronDown,
  Languages,
  Mic,
  MicOff,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const SPANISH_DIALECTS = [
  { code: 'es-MX', name: 'Mexican Spanish', country: 'Mexico', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Castilian Spanish', country: 'Spain', flag: '🇪🇸' },
  { code: 'es-GT', name: 'Guatemalan Spanish', country: 'Guatemala', flag: '🇬🇹' },
  { code: 'es-SV', name: 'Salvadoran Spanish', country: 'El Salvador', flag: '🇸🇻' },
  { code: 'es-HN', name: 'Honduran Spanish', country: 'Honduras', flag: '🇭🇳' },
  { code: 'es-CO', name: 'Colombian Spanish', country: 'Colombia', flag: '🇨🇴' },
  { code: 'es-VE', name: 'Venezuelan Spanish', country: 'Venezuela', flag: '🇻🇪' }
];

// Speech Recognition Languages
const SPEECH_LANGUAGES = [
  { code: 'en-US', name: 'English', flag: '🇺🇸' },
  { code: 'es-MX', name: 'Spanish (Mexico)', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Spanish (Spain)', flag: '🇪🇸' },
  { code: 'es-GT', name: 'Spanish (Guatemala)', flag: '🇬🇹' },
  { code: 'es-SV', name: 'Spanish (El Salvador)', flag: '🇸🇻' },
  { code: 'es-HN', name: 'Spanish (Honduras)', flag: '🇭🇳' },
  { code: 'es-CO', name: 'Spanish (Colombia)', flag: '🇨🇴' },
  { code: 'es-VE', name: 'Spanish (Venezuela)', flag: '🇻🇪' }
];

export const Messages = () => {
  const { api, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState({ employees: [], owners: [] });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoTranslate, setAutoTranslate] = useState(false);
  const [selectedDialect, setSelectedDialect] = useState(SPANISH_DIALECTS[0]);
  const [showDialectPicker, setShowDialectPicker] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Voice-to-Text State
  const [isListening, setIsListening] = useState(false);
  const [speechLanguage, setSpeechLanguage] = useState(SPEECH_LANGUAGES[0]);
  const [showSpeechLanguagePicker, setShowSpeechLanguagePicker] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setNewMessage(transcript);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable it in your browser settings.');
        }
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!speechSupported) {
      toast.error('Speech recognition is not supported in this browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.lang = speechLanguage.code;
      recognitionRef.current.start();
      setIsListening(true);
      toast.info(`Listening in ${speechLanguage.name}...`);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/messages/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await api.get(`/messages/conversation/${conversationId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    // Find recipient from conversation
    const recipient = selectedConversation.participants.find(p => p.id !== user.id);
    if (!recipient) return;

    try {
      await api.post('/messages/send', {
        recipient_id: recipient.id,
        recipient_type: recipient.type,
        content: newMessage,
        translate_to: autoTranslate ? 'es' : null,
        dialect: autoTranslate ? selectedDialect.code : null
      });
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleStartConversation = async (contact) => {
    // Check if conversation exists
    const existing = conversations.find(c => 
      c.participants.some(p => p.id === contact.id)
    );

    if (existing) {
      setSelectedConversation(existing);
    } else {
      // Start new conversation by sending first message
      setSelectedConversation({
        id: 'new',
        participants: [
          { id: user.id, name: user.name, type: 'owner' },
          { id: contact.id, name: contact.name, type: contact.type }
        ]
      });
      setMessages([]);
    }
    setShowContacts(false);
  };

  const handleTranslateMessage = async (message) => {
    try {
      const response = await api.post(`/messages/message/${message.id}/translate?target_language=es&dialect=${selectedDialect.code}`);
      // Update message in state
      setMessages(prev => prev.map(m => 
        m.id === message.id 
          ? { ...m, translated_content: response.data.translated_text, dialect: response.data.dialect }
          : m
      ));
      toast.success('Message translated');
    } catch (error) {
      toast.error('Translation failed');
    }
  };

  const handleSaveAsNote = async (messageId) => {
    try {
      await api.post(`/messages/message/${messageId}/save-as-note`);
      toast.success('Message saved as note');
    } catch (error) {
      toast.error('Failed to save as note');
    }
  };

  const speakText = (text, lang = 'es') => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      // Try to find a voice for the selected dialect
      const voices = speechSynthesis.getVoices();
      const dialectVoice = voices.find(v => v.lang.startsWith(selectedDialect.code.split('-')[0]));
      if (dialectVoice) {
        utterance.voice = dialectVoice;
      }
      speechSynthesis.speak(utterance);
    } else {
      toast.error('Text-to-speech not supported in this browser');
    }
  };

  const filteredContacts = [...contacts.employees, ...contacts.owners].filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-180px)] flex bg-white border border-gray-100" data-testid="messages-page">
      {/* Conversations Sidebar */}
      <div className="w-80 border-r border-gray-100 flex flex-col">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg">Messages</h2>
            <button
              onClick={() => setShowContacts(true)}
              className="p-2 bg-[#C6A87C] text-white rounded-lg hover:bg-[#B09265] transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length > 0 ? (
            conversations.map((conv) => {
              const otherParticipant = conv.participants.find(p => p.id !== user?.id);
              return (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedConversation?.id === conv.id ? 'bg-[#C6A87C]/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{otherParticipant?.name}</p>
                        {conv.unread_count > 0 && (
                          <span className="px-2 py-0.5 bg-[#C6A87C] text-white text-xs rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No conversations yet</p>
              <button
                onClick={() => setShowContacts(true)}
                className="mt-2 text-[#C6A87C] text-sm hover:underline"
              >
                Start a new conversation
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <p className="font-medium">
                    {selectedConversation.participants.find(p => p.id !== user?.id)?.name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {selectedConversation.participants.find(p => p.id !== user?.id)?.type}
                  </p>
                </div>
              </div>

              {/* Translation Settings */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button
                    onClick={() => setShowDialectPicker(!showDialectPicker)}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
                  >
                    <span>{selectedDialect.flag}</span>
                    <span className="hidden sm:inline">{selectedDialect.country}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  <AnimatePresence>
                    {showDialectPicker && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                      >
                        <div className="p-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">Select Dialect</p>
                          {SPANISH_DIALECTS.map((dialect) => (
                            <button
                              key={dialect.code}
                              onClick={() => {
                                setSelectedDialect(dialect);
                                setShowDialectPicker(false);
                              }}
                              className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 text-left ${
                                selectedDialect.code === dialect.code ? 'bg-[#C6A87C]/10' : ''
                              }`}
                            >
                              <span className="text-xl">{dialect.flag}</span>
                              <div>
                                <p className="font-medium text-sm">{dialect.country}</p>
                                <p className="text-xs text-gray-500">{dialect.name}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={() => setAutoTranslate(!autoTranslate)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    autoTranslate 
                      ? 'bg-[#C6A87C] text-white' 
                      : 'border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Languages className="w-4 h-4" />
                  <span className="hidden sm:inline">Auto-Translate</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => {
                const isOwn = message.sender_id === user?.id;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${isOwn ? 'order-2' : ''}`}>
                      <div
                        className={`p-3 rounded-2xl ${
                          isOwn 
                            ? 'bg-[#C6A87C] text-white rounded-br-sm' 
                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}
                      >
                        <p>{message.content}</p>
                        {message.translated_content && (
                          <div className={`mt-2 pt-2 border-t ${isOwn ? 'border-white/20' : 'border-gray-200'}`}>
                            <p className="text-sm italic">{message.translated_content}</p>
                            {message.dialect && (
                              <p className="text-xs mt-1 opacity-70">
                                {SPANISH_DIALECTS.find(d => d.code === message.dialect)?.flag} {message.dialect}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Message Actions */}
                      <div className={`flex items-center gap-2 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-xs text-gray-400">
                          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isOwn && (
                          <span className="text-xs text-gray-400">
                            {message.is_read ? <CheckCheck className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                          </span>
                        )}
                        {!isOwn && (
                          <div className="flex items-center gap-1">
                            {!message.translated_content && (
                              <button
                                onClick={() => handleTranslateMessage(message)}
                                className="p-1 text-gray-400 hover:text-[#C6A87C]"
                                title="Translate"
                              >
                                <Globe className="w-3 h-3" />
                              </button>
                            )}
                            {message.translated_content && (
                              <button
                                onClick={() => speakText(message.translated_content)}
                                className="p-1 text-gray-400 hover:text-[#C6A87C]"
                                title="Speak"
                              >
                                <Volume2 className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={() => handleSaveAsNote(message.id)}
                              className={`p-1 ${message.saved_as_note ? 'text-[#C6A87C]' : 'text-gray-400 hover:text-[#C6A87C]'}`}
                              title="Save as Note"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100">
              {/* Voice Language Selector (when speech is available) */}
              {speechSupported && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-gray-500">Voice input:</span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowSpeechLanguagePicker(!showSpeechLanguagePicker)}
                      className="flex items-center gap-2 px-2 py-1 border border-gray-200 rounded text-xs hover:bg-gray-50"
                    >
                      <span>{speechLanguage.flag}</span>
                      <span>{speechLanguage.name}</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    
                    <AnimatePresence>
                      {showSpeechLanguagePicker && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute left-0 bottom-full mb-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
                        >
                          <div className="p-2 max-h-64 overflow-y-auto">
                            <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">Speak in...</p>
                            {SPEECH_LANGUAGES.map((lang) => (
                              <button
                                key={lang.code}
                                type="button"
                                onClick={() => {
                                  setSpeechLanguage(lang);
                                  setShowSpeechLanguagePicker(false);
                                }}
                                className={`w-full flex items-center gap-2 px-2 py-2 rounded hover:bg-gray-50 text-left ${
                                  speechLanguage.code === lang.code ? 'bg-[#C6A87C]/10' : ''
                                }`}
                              >
                                <span>{lang.flag}</span>
                                <span className="text-sm">{lang.name}</span>
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                {/* Voice-to-Text Button */}
                {speechSupported && (
                  <button
                    type="button"
                    onClick={toggleListening}
                    data-testid="voice-to-text-btn"
                    className={`p-3 rounded-full transition-all ${
                      isListening 
                        ? 'bg-red-500 text-white animate-pulse' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={isListening ? 'Stop listening' : `Speak in ${speechLanguage.name}`}
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                )}
                
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={
                    isListening 
                      ? `Listening in ${speechLanguage.name}...` 
                      : autoTranslate 
                        ? `Type or speak (auto-translate to ${selectedDialect.country} Spanish)` 
                        : 'Type or speak a message...'
                  }
                  className={`flex-1 px-4 py-3 border rounded-full focus:border-[#C6A87C] focus:outline-none ${
                    isListening ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="p-3 bg-[#C6A87C] text-white rounded-full hover:bg-[#B09265] transition-colors disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              
              {/* Listening Indicator */}
              {isListening && (
                <div className="flex items-center justify-center gap-2 mt-2 text-red-500 text-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span>Listening... Speak now</span>
                </div>
              )}
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg">Select a conversation</p>
              <p className="text-sm">or start a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      <AnimatePresence>
        {showContacts && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowContacts(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-xl max-h-[80vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-heading text-lg">New Conversation</h3>
                <button onClick={() => setShowContacts(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4">
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>

                <div className="max-h-96 overflow-y-auto space-y-1">
                  {contacts.employees.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">Employees</p>
                      {filteredContacts.filter(c => c.type === 'employee').map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => handleStartConversation(contact)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                        >
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            <p className="text-sm text-gray-500">{contact.role}</p>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                  
                  {contacts.owners.length > 0 && (
                    <>
                      <p className="text-xs font-semibold text-gray-500 uppercase px-2 py-1 mt-4">Owners</p>
                      {filteredContacts.filter(c => c.type === 'owner').map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => handleStartConversation(contact)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-[#C6A87C]" />
                          </div>
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            <p className="text-sm text-gray-500">{contact.title || 'Owner'}</p>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Messages;
